import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Customer, MachineInfo, Product } from './api/api_pb';
import { customersKey, machineInfosKey, productsKey } from './constant';

interface GlobalContextType {
  MachineInfos: MachineInfo[];
  customers: Customer[];
  products: Product[];
  updateMachineInfos: (newMachineInfo: MachineInfo) => void;
  updateCustomers: (newCustomer: Customer) => void;
  updateProducts: (newProduct: Product) => void;
  findMachineInfoByUuid: (id: string) => MachineInfo | undefined;
  findCustomerByUuid: (mobileNumber: string) => Customer | undefined;
  findProductByUuid: (id: string) => Product | undefined;
}

const GlobalContext = createContext<GlobalContextType | null>(null);

interface GlobalProviderProps {
  children: ReactNode;
}

export const GlobalProvider: React.FC<GlobalProviderProps> = ({ children }) => {
  const [MachineInfos, setMachineInfos] = useState<MachineInfo[]>(() => {
    const saved = localStorage.getItem('MachineInfos');
    return saved ? JSON.parse(saved) : [];
  });

  const [customers, setCustomers] = useState<Customer[]>(() => {
    const saved = localStorage.getItem('customers');
    return saved ? JSON.parse(saved) : [];
  });

  const [products, setProducts] = useState<Product[]>(() => {
    const saved = localStorage.getItem('products');
    return saved ? JSON.parse(saved) : [];
  });

  const safeStringify = (obj: any) => {
    return JSON.stringify(obj, (key, value) =>
      typeof value === 'bigint' ? value.toString() : value
    );
  };


  useEffect(() => {
    localStorage.setItem(machineInfosKey, safeStringify(MachineInfos));
  }, [MachineInfos]);

  useEffect(() => {
    localStorage.setItem(customersKey, safeStringify(customers));
  }, [customers]);

  useEffect(() => {
    localStorage.setItem(productsKey, safeStringify(products));
  }, [products]);

  const updateMachineInfos = (newMachineInfo: MachineInfo) => {
    setMachineInfos((prev) => {
      const index = prev.findIndex((item) => item.uuid === newMachineInfo.uuid);
      const updated = index !== -1
        ? [...prev.slice(0, index), newMachineInfo, ...prev.slice(index + 1)]
        : [...prev, newMachineInfo];

      localStorage.setItem(machineInfosKey, safeStringify(updated));
      return updated;
    });
  };

  const updateCustomers = (newCustomer: Customer) => {
    setCustomers((prev) => {
      const index = prev.findIndex((item) => item.uuid === newCustomer.uuid);
      const updated = index !== -1
        ? [...prev.slice(0, index), newCustomer, ...prev.slice(index + 1)]
        : [...prev, newCustomer];

      localStorage.setItem(customersKey, safeStringify(updated));
      return updated;
    });
  };

  const updateProducts = (newProduct: Product) => {
    setProducts((prev) => {
      const index = prev.findIndex((item) => item.uuid === newProduct.uuid);
      const updated = index !== -1
        ? [...prev.slice(0, index), newProduct, ...prev.slice(index + 1)]
        : [...prev, newProduct];

      localStorage.setItem(productsKey, safeStringify(updated));
      return updated;
    });
  };

  const findMachineInfoById = (uuid: string) => MachineInfos.find(vm => vm.uuid === uuid);
  const findCustomerByUuid = (uuid: string) => customers.find(c => c.uuid === uuid);
  const findProductById = (uuid: string) => products.find(p => p.uuid === uuid);

  return (
    <GlobalContext.Provider
      value={{
        MachineInfos,
        customers,
        products,
        updateMachineInfos,
        updateCustomers,
        updateProducts,
        findMachineInfoByUuid: findMachineInfoById,
        findCustomerByUuid: findCustomerByUuid,
        findProductByUuid: findProductById,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};

export const useGlobalContext = () => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error('useGlobalContext must be used within a GlobalProvider');
  }
  return context;
};
